import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  HStack,
  Badge,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Spacer,
  Avatar,
  List,
  ListItem,
  Divider,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiUserPlus } from 'react-icons/fi';
import DataTable, { Column } from '@/components/common/DataTable';
import UserAutocomplete, { User as AutocompleteUser } from '@/components/common/UserAutocomplete';
import apiClient, { UserRole, TenantCreateParams, TenantUpdateParams } from '@/services/letsparkyClient';
// import { Tenant } from '@letsparky/api-v2-client';
type Tenant = any;
type User = any;
import { useAuthStore } from '@/stores/authStore';
import { useForm } from 'react-hook-form';
import { EntityStatus } from '@letsparky/api-v2-client';
import { useTranslation } from 'react-i18next';


interface TenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant?: Tenant;
  onSave: (data: TenantUpdateParams) => Promise<void>;
  isLoading: boolean;
}

interface TenantAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: Tenant;
  admins: User[];
  onAssignAdmin: (userId: string) => Promise<void>;
  isLoading: boolean;
  availableUsers: User[];
}

const TenantAdminModal: React.FC<TenantAdminModalProps> = ({
  isOpen,
  onClose,
  tenant,
  admins,
  onAssignAdmin,
  isLoading,
  availableUsers,
}) => {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setSelectedUserId('');
    }
  }, [isOpen]);

  const handleAssign = async () => {
    if (!selectedUserId) return;
    await onAssignAdmin(selectedUserId);
    setSelectedUserId(''); // Clear selection after assignment
  };

  const handleUserSelect = (userId: string, user?: AutocompleteUser) => {
    setSelectedUserId(userId);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('tenants.manageAdminsTitle', { name: tenant.name })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={6}>
            <Heading size="sm" mb={4}>{t('tenants.currentAdministrators')}</Heading>
            {admins.length > 0 ? (
              <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>{t('tenants.columnAdmin')}</Th>
                      <Th>{t('user.email')}</Th>
                      <Th>{t('common.phone')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {admins.map((admin) => (
                      <Tr key={admin.id}>
                        <Td>
                          <Flex align="center">
                            <Avatar 
                              size="sm" 
                              name={`${admin.name || ''} ${admin.surname || ''}`} 
                              src={admin.photo} 
                              mr={2} 
                            />
                            <Box>
                              <Text fontWeight="medium">{admin.name} {admin.surname || ''}</Text>
                            </Box>
                          </Flex>
                        </Td>
                        <Td>{admin.email}</Td>
                        <Td>{admin.phone_number || '-'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
                <Text color="gray.500">{t('tenants.noAdmins')}</Text>
                <Text fontSize="sm" mt={1}>{t('tenants.assignAdminHint')}</Text>
              </Box>
            )}
          </Box>
          
          <Divider my={6} />
          
          <Box>
            <Heading size="sm" mb={4}>{t('tenants.assignNewAdministrator')}</Heading>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('tenants.selectUser')}</FormLabel>
                <UserAutocomplete
                  users={availableUsers.map(user => ({
                    id: user.id,
                    name: user.name,
                    surname: user.surname,
                    email: user.email,
                    phone_number: user.phone_number,
                    role: user.role,
                    photo: user.photo,
                  }))}
                  value={selectedUserId}
                  onChange={handleUserSelect}
                  placeholder={t('tenants.searchAdminsPlaceholder')}
                  isLoading={false}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('tenants.onlyNonAdminsShown')}
                </Text>
              </FormControl>
            </VStack>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!selectedUserId}
            onClick={handleAssign}
            isLoading={isLoading}
            leftIcon={<FiUserPlus />}
          >
            {t('tenants.assignAsAdministrator')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const TenantModal: React.FC<TenantModalProps> = ({
  isOpen,
  onClose,
  tenant,
  onSave,
  isLoading,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TenantUpdateParams>({
    defaultValues: tenant
      ? {
          name: tenant.name,
          status: tenant.status as EntityStatus,
          logo: tenant.logo,
          main_color: tenant.main_color,
          payment_config: tenant.payment_config,
          reservation_config: tenant.reservation_config,
        }
      : {
          status: EntityStatus.ACTIVE,
        },
  });

  useEffect(() => {
    if (isOpen) {
      const defaultValues = tenant
        ? {
            name: tenant.name,
            status: tenant.status,
            logo: tenant.logo,
            main_color: tenant.main_color,
            payment_config: tenant.payment_config,
            reservation_config: tenant.reservation_config,
          }
        : {
            status: EntityStatus.ACTIVE,
          };

      reset(defaultValues);

      // Set pos_alias default value if creating new tenant or if not set
      if (!tenant || !tenant.payment_config?.pos_alias) {
        setValue('payment_config', {
          ...defaultValues.payment_config,
          pos_alias: 'default'
        });
      }
    }
  }, [isOpen, tenant, reset, setValue]);

  const onSubmit = async (data: TenantUpdateParams) => {
    // Ensure payment_config has the correct structure
    const updatedData = {
      ...data,
      payment_config: {
        ...data.payment_config,
        pos_alias: data.payment_config?.pos_alias || 'default',
      },
    };
    await onSave(updatedData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>{tenant ? t('tenants.editTenant') : t('tenants.addTenant')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.name} isRequired>
                <FormLabel>{t('common.name')}</FormLabel>
                <Input
                  {...register('name', {
                    required: t('tenants.nameRequired'),
                  })}
                />
                {errors.name && <FormErrorMessage>{errors.name.message}</FormErrorMessage>}
              </FormControl>

              <FormControl isInvalid={!!errors.status} isRequired>
                <FormLabel>{t('common.status')}</FormLabel>
                <Select
                  {...register('status', {
                    required: t('tenants.statusRequired'),
                  })}
                >
                  <option value="ACTIVE">{t('tenants.statusActiveOption')}</option>
                  <option value="INACTIVE">{t('tenants.statusInactiveOption')}</option>
                </Select>
                {errors.status && <FormErrorMessage>{errors.status.message}</FormErrorMessage>}
              </FormControl>

              <FormControl>
                <FormLabel>{t('tenants.logoUrl')}</FormLabel>
                <Input {...register('logo')} />
              </FormControl>

              <FormControl>
                <FormLabel>{t('tenants.mainColor')}</FormLabel>
                <Input
                  {...register('main_color')}
                  placeholder="#FFFFFF"
                  type="text"
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('tenants.paymentPos')}</FormLabel>
                <Input
                  {...register('payment_config.pos_alias')}
                  placeholder="default"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('tenants.paymentPosHint')}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isLoading}
            >
              {tenant ? t('common.update') : t('common.create')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

const Tenants: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isAdminModalOpen, 
    onOpen: onAdminModalOpen, 
    onClose: onAdminModalClose 
  } = useDisclosure();
  
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTenantForAdmins, setSelectedTenantForAdmins] = useState<Tenant | null>(null);
  const [tenantAdmins, setTenantAdmins] = useState<User[]>([]);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [isAssigningAdmin, setIsAssigningAdmin] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  // Only SYSTEM users should access this page
  if (!isSystem) {
    return (
      <Box p={8} textAlign="center">
        <Heading size="md">{t('tenants.accessDenied')}</Heading>
        <Box mt={4}>{t('tenants.accessDeniedDescription')}</Box>
      </Box>
    );
  }

  const fetchTenants = async () => {
    setIsLoading(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      const response = await apiClient.tenants.listPaginated({ 
        page: currentPage, 
        limit: pageSize
      });
      setTenants(response.data);
      setTotalItems(response.pagination.total);
    } catch (error) {
      toast({
        title: t('tenants.fetchError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      const response = await apiClient.users.listPaginated({ page: 1, limit: 2000, sort: 'updated_at', order: 'desc' as const });
      setAvailableUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: t('tenants.fetchUsersError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchTenantAdmins = async (tenantId: string) => {
    setIsLoadingAdmins(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      // Here you would call your API to get tenant admins
      // For example: const response = await apiClient.tenants.getAdmins(tenantId);
      
      // Mocking the API call for now with fetching all users and filtering
      const response = await apiClient.users.listPaginated({ page: 1, limit: 2000, sort: 'created_at', order: 'desc' as const });
      // Filter users who are assigned to this tenant and have TENANT_ADMIN role
      const admins = response.data.filter(
        (user: User) => user.tenant_id === tenantId && user.role === 'TENANT_ADMIN'
      );
      setTenantAdmins(admins);
    } catch (error) {
      toast({
        title: t('tenants.fetchAdminsError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setTenantAdmins([]);
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTenants();
      fetchAvailableUsers();
    }
  }, [currentPage, pageSize, searchQuery, token]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('tenants.deleteConfirm'))) {
      try {
        if (!token) throw new Error('Authentication token not available');
        apiClient.client.setToken(token);
        await apiClient.tenants.delete(id);
        toast({
          title: t('tenants.deleted'),
          status: 'success',
          duration: 3000,
        });
        fetchTenants();
      } catch (error) {
        toast({
          title: t('tenants.deleteError'),
          description: error instanceof Error ? error.message : t('common.unknown'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    onOpen();
  };

  const handleAddTenant = () => {
    setSelectedTenant(undefined);
    onOpen();
  };

  const handleSaveTenant = async (data: TenantUpdateParams) => {
    setIsSaving(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      if (selectedTenant) {
        // Update existing tenant
        await apiClient.tenants.update(selectedTenant.id, data);
        toast({
          title: t('tenants.updated'),
          status: 'success',
          duration: 3000,
        });
      } else {
        // Create new tenant
        await apiClient.tenants.create(data as TenantCreateParams);
        toast({
          title: t('tenants.created'),
          status: 'success',
          duration: 3000,
        });
      }
      
      onClose();
      fetchTenants();
    } catch (error) {
      toast({
        title: selectedTenant ? t('tenants.saveErrorUpdate') : t('tenants.saveErrorCreate'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageAdmins = (tenant: Tenant) => {
    setSelectedTenantForAdmins(tenant);
    fetchTenantAdmins(tenant.id);
    onAdminModalOpen();
  };

  const handleAssignAdmin = async (userId: string) => {
    if (!selectedTenantForAdmins) return;
    
    setIsAssigningAdmin(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      // Call the API to assign user as tenant admin
      await apiClient.tenants.assignUser(selectedTenantForAdmins.id, {
        user_id: userId,
        role: UserRole.TENANT_ADMIN
      });
      
      toast({
        title: t('tenants.assignSuccess'),
        status: 'success',
        duration: 3000,
      });
      
      // Refresh the tenant admins list
      fetchTenantAdmins(selectedTenantForAdmins.id);
    } catch (error) {
      toast({
        title: t('tenants.assignError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAssigningAdmin(false);
    }
  };

  const columns: Column<Tenant>[] = [
    {
      header: t('common.name'),
      accessor: 'name',
    },
    {
      header: t('common.status'),
      accessor: (tenant) => (
        <Badge colorScheme={tenant.status === 'ACTIVE' ? 'green' : 'red'}>
          {tenant.status === 'ACTIVE' ? t('tenants.statusActiveOption') : t('tenants.statusInactiveOption')}
        </Badge>
      ),
    },
    {
      header: t('tenants.columnCreated'),
      accessor: (tenant) => new Date(tenant.created_at).toLocaleDateString(),
    },
  ];

  const actionButtons = (tenant: Tenant) => (
    <>
      <IconButton
        aria-label={t('tenants.ariaManageAdmins')}
        icon={<FiUsers />}
        size="sm"
        colorScheme="teal"
        variant="ghost"
        onClick={() => handleManageAdmins(tenant)}
        mr={1}
      />
      <IconButton
        aria-label={t('tenants.ariaEditTenant')}
        icon={<FiEdit2 />}
        size="sm"
        colorScheme="blue"
        variant="ghost"
        onClick={() => handleEditTenant(tenant)}
      />
      <IconButton
        aria-label={t('tenants.ariaDeleteTenant')}
        icon={<FiTrash2 />}
        size="sm"
        colorScheme="red"
        variant="ghost"
        onClick={() => handleDelete(tenant.id)}
      />
    </>
  );

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6}>
        <Heading size="lg">{t('tenants.managementTitle')}</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={handleAddTenant}>
          {t('tenants.addTenant')}
        </Button>
      </HStack>

      <DataTable
        columns={columns}
        data={tenants}
        keyField="id"
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearch={handleSearch}
        actionButtons={actionButtons}
      />

      <TenantModal
        isOpen={isOpen}
        onClose={onClose}
        tenant={selectedTenant}
        onSave={handleSaveTenant}
        isLoading={isSaving}
      />

      {selectedTenantForAdmins && (
        <TenantAdminModal
          isOpen={isAdminModalOpen}
          onClose={onAdminModalClose}
          tenant={selectedTenantForAdmins}
          admins={tenantAdmins}
          onAssignAdmin={handleAssignAdmin}
          isLoading={isAssigningAdmin}
          availableUsers={availableUsers.filter(
            (user) => !tenantAdmins.some((admin) => admin.id === user.id)
          )}
        />
      )}
    </Box>
  );
};

export default Tenants; 